import React from 'react';
import { Datepicker, locale } from '@mobiscroll/react';
import '@mobiscroll/react/dist/css/mobiscroll.min.css';
import { IconButton, InputAdornment, TextField, TextFieldProps, useTheme } from '@material-ui/core';
import { CalendarToday } from '@material-ui/icons';
import './StyleOverride.css';
import { TFunction } from 'react-i18next';
import { formatRFC3339 } from 'date-fns';
import { MbscDatepickerOptions } from '@mobiscroll/react/dist/src/core/components/datepicker/datepicker';

interface DateAndTimePickerProps extends MbscDatepickerOptions {
  isSmallDevice?: boolean;
  onChange: (e: string) => void;
  t: TFunction;
  inputProps?: TextFieldProps;
}

// Note: See https://docs.mobiscroll.com/react/datepicker#options for more documentation

/**
 * Custom AskPorter datepicker component, using MUI TextField and Mobiscroll datepicker.
 *
 * main customization is the controls props, which is an array that controls available picker widgets.
 *  - Most of the time we just want ['calendar', 'time'].
 *  - dateWidget is the only component that currently uses just ['calendar'].
 *  - There are future uses planned for ['calendar', 'timegrid']
 *
 * ```tsx
 * <DateAndTimePicker
 *  value={'2020-01-01T00:00:00.000Z'}
 *  isSmallDevice={true}
 *  onChange={(e: string) => {}}
 *  inputProps={{
 *    label: 'Date',
 *    variant: 'outlined',
 *    id: 'date-datetime',
 *    inputProps: {
 *      key: new Date(afterValue).getTime(),
 *    },
 *   stepMinute={30}
 *   controls={['calendar', 'time']}
 *   t={t}
 *  }}
 * />
 * ```
 *
 * @param param0
 * @returns
 */
const DateAndTimePicker: React.FC<DateAndTimePickerProps> = ({
  isSmallDevice = false,
  onChange,
  value,
  inputProps,
  children,
  t,
  ...props
}) => {
  const theme = useTheme();
  const anchor = React.useRef(null);
  const mobiscroll = React.useRef(null);
  const [isOpen, setIsOpen] = React.useState(false);

  // Note: Mobiscroll doesn't yet support a way of updating theme colours on the fly with JS.
  // Using css variables is the cleanest alternative, but it's not supported by some older browsers.
  React.useEffect(() => {
    document.documentElement.style.setProperty('--mbsc-color', theme.palette.primary.main);
  }, [theme]);

  // Note: By default, mobiscroll will use 'en-US' as the locale. I think UK date time is better.
  const languageCode = localStorage.getItem('selectedLanguage');
  const dateLocale = locale[languageCode] || locale[languageCode?.split('-')[0]] || locale['en-GB'];

  const onInputChange = (e: { target: { value: string } }) => {
    mobiscroll.current.setVal(e.target.value);
    setIsOpen(false);
  };

  const setNewDate = () => {
    const newTime = mobiscroll.current.getVal();
    if (!newTime) return;
    // Note: Mobiscroll returns a string with current time seconds and milliseconds, and we don't want those.
    newTime.setSeconds(0.0);
    newTime.setMilliseconds(0.0);
    onChange(formatRFC3339(newTime, { fractionDigits: 3 }));
    setIsOpen(false);
  };

  const handleClearTime = () => {
    onChange(null);
    mobiscroll.current.setVal(null);
    setIsOpen(false);
  };

  // If there is only 1 control type to pick, the picker doesn't need
  // to confirm the date, can change the value and close.
  const onChangeHandler = () => props.controls.length === 1 && setNewDate();

  const onKeyDown = ({ key }: React.KeyboardEvent) => key === 'Enter' && setNewDate();

  const { InputProps } = inputProps;

  return (
    <Datepicker
      ref={mobiscroll}
      theme="material"
      themeVariant="light"
      anchor={anchor.current}
      display="anchored"
      locale={dateLocale}
      showOnFocus={false}
      defaultValue={value && new Date(value)}
      showOnClick={false}
      returnFormat="jsdate"
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      onChange={onChangeHandler}
      touchUi={isSmallDevice}
      buttons={[
        { text: t('ns.common:date_picker:ok'), handler: setNewDate, keyCode: 'enter' },
        { text: t('ns.common:date_picker:clear'), handler: handleClearTime },
        { text: t('ns.common:date_picker:cancel'), handler: () => mobiscroll.current.close(), keyCode: 'esc' },
      ]}
      inputComponent={TextField}
      {...props}
      inputProps={{
        ...inputProps,
        onChange: onInputChange,
        onBlur: setNewDate,
        onKeyDown: onKeyDown,
        InputProps: {
          ...InputProps,
          ref: anchor,
          // Note: Mui calendar input type is 'tel', possibly because setting it to
          // 'date' causes the input to contain native browser date formatting.
          type: 'tel',
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label={t('ns.common:date_picker:toggle_visibility')}
                onClick={() => setIsOpen(true)}
                edge="end"
                disabled={props?.disabled}
              >
                <CalendarToday />
              </IconButton>
            </InputAdornment>
          ),
        },
        InputLabelProps: { shrink: !!value },
      }}
    />
  );
};

export default DateAndTimePicker;
